import { Select } from '@loadsmart/loadsmart-ui'
import { Selectable } from '@loadsmart/loadsmart-ui/dist/hooks/useSelectable'
import { TranslationMapContentType } from 'common/types/kraken-core/TranslationMap'
import { useSearchContentTypes } from 'pages/TranslationMaps/api'
import { useEffect, useState, useMemo } from 'react'

export interface ContentTypeSelectorProps {
  selected?: {
    label?: string
    value?: string
  }
  onSelect: (selected: TranslationMapContentType | null) => void
  disabled?: boolean
  placeholder?: string
  style?: React.CSSProperties
}

export default function ContentTypeSelector({
  selected,
  onSelect,
  disabled = false,
  placeholder = 'Select a Content Type',
  style,
}: ContentTypeSelectorProps) {
  const [selectedValue, setSelectedValue] = useState<Selectable | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  // Get content types from the API
  const { data: contentTypesList, isLoading: isContentTypesLoading } = useSearchContentTypes()

  // Memoize the selected value to avoid unnecessary re-renders
  const memoizedSelected = useMemo(() => {
    if (selected?.value && selected?.label) {
      return {
        value: selected.value,
        label: selected.label,
      }
    }
    return null
  }, [selected?.value, selected?.label])

  // Handle initialization with value only
  useEffect(() => {
    const initializeWithValue = async () => {
      // If we have both value and label, use the memoized version
      if (memoizedSelected) {
        setSelectedValue(memoizedSelected as Selectable)
        return
      }

      // If we have only value but no label, and content types are loaded, find the label
      if (selected?.value && !selected?.label && contentTypesList) {
        setIsLoading(true)
        try {
          const contentType = contentTypesList.find(ct => ct.content_type === selected.value)
          if (contentType) {
            const newSelected = {
              value: contentType.content_type,
              label: contentType.description || contentType.content_type,
            }
            setSelectedValue(newSelected as Selectable)
          } else {
            // If content type not found, use the value as label
            setSelectedValue({
              value: selected.value,
              label: selected.value,
            } as Selectable)
          }
        } catch (error) {
          console.error('Error finding content type:', error)
          // Fallback to using value as label
          setSelectedValue({
            value: selected.value,
            label: selected.value,
          } as Selectable)
        } finally {
          setIsLoading(false)
        }
      } else if (!selected?.value) {
        // Clear selection if no value provided
        setSelectedValue(null)
      }
    }

    initializeWithValue()
  }, [selected?.value, selected?.label, memoizedSelected, contentTypesList])

  const handleChange = (e: any) => {
    const value = e.target.value ? (e.target.value as any).value : null
    const selectedItem = contentTypesList?.find(ct => ct.content_type === value) || null

    setSelectedValue(
      selectedItem
        ? ({
            value: selectedItem.content_type,
            label: selectedItem.description || selectedItem.content_type,
          } as Selectable)
        : null
    )
    onSelect(selectedItem)
  }

  // Create options from content types list
  const options = useMemo(() => {
    if (!contentTypesList) return []
    return contentTypesList.map(ct => ({
      label: ct.description || ct.content_type,
      value: ct.content_type,
    }))
  }, [contentTypesList])

  return (
    <Select
      name="select_content_type"
      onChange={handleChange}
      value={selectedValue}
      options={options}
      placeholder={placeholder}
      disabled={disabled || isLoading || isContentTypesLoading}
      style={{
        maxWidth: '170px',
        ...style,
      }}
    />
  )
}
